import { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Route as RouteIcon, MapPin, Loader2 } from "lucide-react";

const startIcon = new L.DivIcon({
  html: `<div style="background:#10b981;width:22px;height:22px;border-radius:50%;border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>`,
  className: "trip-replay-start",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});
const endIcon = new L.DivIcon({
  html: `<div style="background:#ef4444;width:22px;height:22px;border-radius:50%;border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>`,
  className: "trip-replay-end",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString();
}

function fmtDuration(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  const ms = new Date(endIso) - new Date(startIso);
  if (Number.isNaN(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins - h * 60}m`;
}

/**
 * Admin Trip Replay viewer.
 *
 * Backend: GET /api/admin/bookings/{id}/trail → { booking_id, count, points: [
 *   { lat, lng, speed_kph?, heading_deg?, recorded_at, driver_id }, ...
 * ] } ordered by recorded_at asc.
 */
export default function TripReplayDialog({ open, onClose, bookingId, auth, API }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !bookingId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/admin/bookings/${bookingId}/trail`, auth())
      .then((res) => {
        if (cancelled) return;
        setPoints(Array.isArray(res.data?.points) ? res.data.points : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || e.message);
        setPoints([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, bookingId, auth, API]);

  const hasPoints = points.length > 0;
  const polyline = hasPoints ? points.map(p => [p.lat, p.lng]) : [];
  const center = hasPoints
    ? [points[Math.floor(points.length / 2)].lat, points[Math.floor(points.length / 2)].lng]
    : [52.5, -1.5]; // UK centre fallback

  const start = hasPoints ? points[0] : null;
  const end = hasPoints ? points[points.length - 1] : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="trip-replay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RouteIcon className="w-5 h-5 text-orange-500" />
            Trip Replay
          </DialogTitle>
          <DialogDescription>
            Booking: <span className="font-mono">{bookingId?.slice(0, 8).toUpperCase()}</span>
            {hasPoints && (
              <>
                {" "}— <Badge className="ml-1 bg-slate-100 text-slate-700" data-testid="trip-replay-count">
                  {points.length} GPS points
                </Badge>{" "}
                <span className="text-xs text-slate-500" data-testid="trip-replay-duration">
                  duration {fmtDuration(start?.recorded_at, end?.recorded_at)}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center" data-testid="trip-replay-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-orange-500" />
            <p className="text-xs text-slate-500 mt-2">Loading trip trail…</p>
          </div>
        )}

        {!loading && error && (
          <div className="py-6 text-center text-sm text-red-600" data-testid="trip-replay-error">{error}</div>
        )}

        {!loading && !error && !hasPoints && (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="trip-replay-empty">
            <MapPin className="w-6 h-6 mx-auto text-slate-300 mb-2" />
            No GPS breadcrumbs recorded for this booking yet.
            <p className="text-xs text-slate-400 mt-1">
              Trails are captured when the assigned driver is online with GPS enabled.
            </p>
          </div>
        )}

        {!loading && !error && hasPoints && (
          <div className="space-y-3">
            <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height: 360 }}>
              <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                <TileLayer
                  attribution="&copy; OpenStreetMap"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Polyline positions={polyline} pathOptions={{ color: "#f97316", weight: 4, opacity: 0.85 }} />
                {start && (
                  <Marker position={[start.lat, start.lng]} icon={startIcon}>
                    <Popup>
                      <div className="text-xs">
                        <p className="font-semibold text-emerald-700">Start</p>
                        <p>{fmtTime(start.recorded_at)}</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
                {end && (
                  <Marker position={[end.lat, end.lng]} icon={endIcon}>
                    <Popup>
                      <div className="text-xs">
                        <p className="font-semibold text-red-700">End</p>
                        <p>{fmtTime(end.recorded_at)}</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-100">
                <p className="text-emerald-700 font-semibold">Start</p>
                <p className="font-mono text-slate-600">{start?.lat.toFixed(4)}, {start?.lng.toFixed(4)}</p>
                <p className="text-slate-500">{fmtTime(start?.recorded_at)}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-2 border border-red-100">
                <p className="text-red-700 font-semibold">End</p>
                <p className="font-mono text-slate-600">{end?.lat.toFixed(4)}, {end?.lng.toFixed(4)}</p>
                <p className="text-slate-500">{fmtTime(end?.recorded_at)}</p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
